import { useEffect, useRef, useState } from 'react';
import { useLang, type Lang } from '../../../i18n';
import { get } from '../../../services/api';
import useIntersectionFadeIn from '../../../hooks/useIntersectionFadeIn';

interface StatItem { value: string | number; suffix?: string; label: string }
interface StatsApi { items?: Array<StatItem>; results?: Array<StatItem> }

const LANG_MAP: Record<Lang, string> = { 'uz-Latn': 'uz_lat', 'uz-Cyrl': 'uz_cyr', ru: 'ru', en: 'en' };

function parseNumeric(v: string | number): { num: number | null; decimals: number } {
  if (typeof v === 'number') return { num: v, decimals: 0 };
  const s = String(v ?? '').trim();
  const m = s.match(/-?\d+([.,]\d+)?/);
  if (!m) return { num: null, decimals: 0 };
  const numStr = m[0].replace(',', '.');
  const num = parseFloat(numStr);
  if (!Number.isFinite(num)) return { num: null, decimals: 0 };
  const dotIdx = numStr.indexOf('.');
  const decimals = dotIdx >= 0 ? numStr.length - dotIdx - 1 : 0;
  return { num, decimals };
}

function useCountUp(target: number | null, decimals: number, active: boolean, duration = 1400): number {
  const [v, setV] = useState(target ?? 0);
  const startedRef = useRef(false);
  useEffect(() => {
    if (target === null) return;
    if (!active || startedRef.current) {
      if (!active) setV(target); // если анимация не активирована — показываем сразу
      return;
    }
    startedRef.current = true;
    const start = performance.now();
    let raf = 0;
    const factor = Math.pow(10, decimals);
    const tick = (now: number) => {
      const elapsed = now - start;
      const t = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setV(Math.round(eased * target * factor) / factor);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, decimals, active, duration]);
  return v;
}

function Stat({ item, active }: { item: StatItem; active: boolean }) {
  const { num, decimals } = parseNumeric(item.value);
  const value = useCountUp(num, decimals, active);
  // Если значение не числовое (например "24/7") — показываем оригинальную строку.
  const display = num === null ? String(item.value ?? '') : value.toFixed(decimals);
  return (
    <div className="trust__item">
      <div className="trust__value">
        <span className="trust__num">{display}</span>
        {item.suffix ? <span className="trust__suffix">{item.suffix}</span> : null}
      </div>
      <div className="eyebrow trust__label">{item.label}</div>
    </div>
  );
}

export default function TrustBarSection() {
  const { lang } = useLang();
  const [items, setItems] = useState<StatItem[]>([]);
  const rootRef = useRef<HTMLElement>(null);
  const visible = useIntersectionFadeIn(rootRef, { threshold: 0.3 });

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await get<StatsApi>(`/about/stats/?lang=${LANG_MAP[lang]}`);
        if (cancel) return;
        const list = res?.items || res?.results || [];
        setItems(list.slice(0, 4));
      } catch {
        if (!cancel) setItems([]);
      }
    })();
    return () => { cancel = true; };
  }, [lang]);

  if (!items.length) return null;

  return (
    <section ref={rootRef} className="trust section--surface-2">
      <div className="container trust__inner">
        {items.map((it, idx) => (
          // active=true даже если IntersectionObserver не сработал — пользователь
          // должен видеть число, а не «0», даже без скролла.
          <Stat key={`${it.label}-${idx}`} item={it} active={visible || true} />
        ))}
      </div>
    </section>
  );
}
